import React, { useEffect, useRef, useState } from "react";
import "./style.scss";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import Timeline from "./Timeline";
import { ICalendarHeatmapProps } from "./calendar-heatmap-editor/CalendarHeatmapEditor";

const CalendarHeatmap = (props: ICalendarHeatmapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLarge, setIsLarge] = useState<boolean>(true);
  const [currentStartDate, setCurrentStartDate] = useState<Date>(props.startDate);
  const [currentEndDate, setCurrentEndDate] = useState<Date>(props.endDate);
  const [minMaxValues, setMinMaxValues] = useState<[number, number] | undefined>(undefined);

  const { data: dataset, isLoading } = useGetDataInfoRange([props.identifier], {
    timeStart: props.startDate.toString(),
    timeEnd: props.endDate.toString(),
  });

  useEffect(() => {
    if (dataset && dataset[0] && dataset[0].data.length > 0) {
      const values = dataset[0].data.map(row => row.value);
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      setMinMaxValues([minValue, maxValue]);
    }
  }, [dataset]);

  const startDate = new Date(currentStartDate);
  const endDate = new Date(currentEndDate);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setIsLarge(containerRef.current.offsetWidth >= 1000);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const colorFunc = (value: number) => {
    const maxValue = props.maxValue ?? minMaxValues?.[1] ?? 0;
    const minValue = props.minValue ?? minMaxValues?.[0] ?? 0;
    const ratio = (value - minValue) / (maxValue - minValue);
    const minColor = parseColor(props.lowValueColor);
    const maxColor = parseColor(props.highValueColor);
    const r = Math.round(minColor.r * (1 - ratio) + maxColor.r * ratio);
    const g = Math.round(minColor.g * (1 - ratio) + maxColor.g * ratio);
    const b = Math.round(minColor.b * (1 - ratio) + maxColor.b * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const gradientColors = Array.from({ length: 10 }, (_, i) => {
    const ratio = i / 9;
    const minColor = parseColor(props.lowValueColor);
    const maxColor = parseColor(props.highValueColor);
    const r = Math.round(minColor.r * (1 - ratio) + maxColor.r * ratio);
    const g = Math.round(minColor.g * (1 - ratio) + maxColor.g * ratio);
    const b = Math.round(minColor.b * (1 - ratio) + maxColor.b * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  });

  const yearsInSelectedRange = Array.from(
    { length: endDate.getFullYear() - startDate.getFullYear() + 1 },
    (_, i) => {
      return startDate.getFullYear() + i;
    }
  );

  const getRangeForYear = (year: number): [Date, Date] => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    if (year === startDate.getFullYear() && year === endDate.getFullYear()) {
      return [startDate, endDate];
    }
    if (year === startDate.getFullYear()) {
      return [startDate, yearEnd];
    }
    if (year === endDate.getFullYear()) {
      return [yearStart, endDate];
    }
    return [yearStart, yearEnd];
  };

  return (
    <div ref={containerRef} className={`container ${isLarge ? "large" : "small"}`}>
      <div className="flex flex-row gap-3 justify-content-between">
        {props.showDateSelection && (
          <div className="flex flex-row gap-2">
            <div className="calendar-container flex flex-column gap-1 w-5">
              <b style={{ padding: "0 0 0 5px" }}>From</b>
              <CalendarWrapper
                value={startDate}
                onChange={e => {
                  setCurrentStartDate(e);
                }}
                dateFormat="yy-mm-dd"
              />
            </div>
            <div className="calendar-container flex flex-column gap-1 w-5">
              <b style={{ padding: "0 0 0 5px" }}>To</b>
              <CalendarWrapper
                value={endDate}
                onChange={e => {
                  setCurrentEndDate(e);
                }}
                dateFormat="yy-mm-dd"
              />
            </div>
          </div>
        )}
        <div className="flex flex-row gradient-bar justify-content-center m-auto">
          {props.staticMinMaxTitles === true ? (
            <div>{props.lowValueTitle}</div>
          ) : (
            <div>{minMaxValues && minMaxValues[0]}</div>
          )}
          {gradientColors.map(color => (
            <div key={color} className="gradient-box" style={{ backgroundColor: color }} />
          ))}
          {props.staticMinMaxTitles ? (
            <div>{props.highValueTitle}</div>
          ) : (
            <div>{minMaxValues && minMaxValues[1]}</div>
          )}
        </div>
        <div
          className={`flex ${props.showDateSelection ? "flex-column" : "flex-row"} gap-2 m-auto`}
        >
          <div className="flex flex-row gap-2">
            <div className="running-status" />
            <div>running</div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="planned-status">.</div>
            <div>planned</div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="no-status" />
            <div>no-status</div>
          </div>
        </div>
      </div>
      {!isLoading && dataset && dataset[0].data.length > 0 && yearsInSelectedRange.length === 1 && (
        <div className="flex flex-row w-full">
          <div className="timeline-text">{yearsInSelectedRange[0]}</div>
          <Timeline
            range={getRangeForYear(yearsInSelectedRange[0])}
            data={dataset[0].data.map(item => ({ key: new Date(item.key), value: item.value }))}
            colorFunc={colorFunc}
          />
        </div>
      )}
      {!isLoading && dataset && dataset[0].data.length > 0 && yearsInSelectedRange.length > 1 && (
        <div>
          {yearsInSelectedRange.map(year => (
            <div key={year} className="flex flex-row">
              <div className="timeline-text">{year}</div>
              <Timeline
                range={getRangeForYear(year)}
                data={dataset[0].data.map(item => ({ key: new Date(item.key), value: item.value }))}
                colorFunc={colorFunc}
              />
            </div>
          ))}
        </div>
      )}
      {isLoading && <div>Loading...</div>}
      {!isLoading && !dataset && <div>Selected dataset not found</div>}
    </div>
  );
};

const parseColor = (hex: string) => {
  let modifiedHex = hex;
  if (!modifiedHex) return { r: 0, g: 0, b: 0 };
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  modifiedHex = modifiedHex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(modifiedHex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

export default CalendarHeatmap;
